import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faLink, faLock } from '@fortawesome/pro-light-svg-icons';
import { Container } from 'react-bootstrap';
import { useNavigate, useParams } from "react-router-dom";
import ProgressWrapper from '../../components/ProgressWrapper.js';
import { useAxios } from '../../utils/hooks.ts';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { confirmPopup } from 'primereact/confirmpopup';
import FlagDetails from '../../components/flags/FlagDetails.js';
import CreateEditFlag from '../../components/flags/CreateEditFlag.js';

export default function FlagDetailsPage() {
    const [ loading, setLoading ] = React.useState(true);
    const [ fetchFlag, setFetchFlag ] = React.useState(false);
    const [ flags, setFlags ] = React.useState([]);
    const [ createVisible, setCreateVisible ] = React.useState(false);
    const [ detailsVisible, setDetailsVisible ] = React.useState(false);
    const [ selectedFlag, setSelectedFlag ] = React.useState(null);
    const { id } = useParams();
    const axiosInstance = useAxios();
    const navigate = useNavigate();
    const toast = React.useRef(null);

    const flipFetchFlag = () => {
        setFetchFlag(!fetchFlag);
    }

    const toggleVisible = () => {
        setCreateVisible(!createVisible);
    }

    const toggleDetails = () => {
        setDetailsVisible(!detailsVisible);
    }

    const displayToast = (msg) => {
        toast.current.show(msg);
        flipFetchFlag();
    }

    const toggleFlag = (flag) => {
        const data = {
            id: flag.id,
            flagId: flag.flagId,
            environmentId: flag.environmentId,
            enabled: !flag.enabled
        }
        axiosInstance.current.put("/flags/enable", data).then(() => {
            flag.enabled = data.enabled;
            displayToast({
                severity: 'success',
                summary: 'Flag updated!',
                detail: 'Flag has been successfully updated.'
            });
        })
        .catch((err) => {
            console.log(err);
            displayToast({
                severity: 'error',
                summary: 'Unexpected error!',
                detail: err.response ? err.response.data : 'Please try again or contact support.'
            })
        })
    }

    const archiveFlag = (flag) => {
        const data = {
            id: flag.id,
            flagId: flag.flagId,
            environmentId: flag.environmentId,
            archived: !flag.archived
        }
        axiosInstance.current.put("/flags/archive", data).then(() => {
            flag.archived = data.archived;
            displayToast({
                severity: 'success',
                summary: 'Flag updated!',
                detail: 'Flag has been successfully updated.'
            });
        })
        .catch((err) => {
            console.log(err);
            displayToast({
                severity: 'error',
                summary: 'Unexpected error!',
                detail: err.response ? err.response.data : 'Please try again or contact support.'
            })
        })
    }

    const editFlag = (flag) => {
        setSelectedFlag(flag);
        setCreateVisible(true);
    }

    const environmentNameBody = (rowData) => {
        if (rowData.environmentProtected) {
            return <div className="danger"><b>{rowData.environmentName}<span className="ml10"><FontAwesomeIcon icon={faLock} /></span></b></div>
        } else {
            return <div>{rowData.environmentName}</div>
        }
    }

    const enabledBody = (rowData) => {
        return <InputSwitch id="enabled" checked={rowData.enabled} onChange={() => toggleFlag(rowData)} disabled={isDisabled(rowData)} />
    }

    const enabledLastBody = (rowData) => {
        return rowData.enabledOn;
    }

    const archivedBody = (rowData) => {
        return <InputSwitch id="archived" checked={rowData.archived} onChange={() => archiveFlag(rowData)} disabled={isDisabled(rowData)} />
    }

    const viewBody = (rowData) => {
        return <Button label="View Details" className="p-button-text" icon="pi pi-angle-right" onClick={() => {setSelectedFlag(rowData); setDetailsVisible(true);}} disabled={isDisabled(rowData)} />
    }

    const editBody = (rowData) => {
        return <Button className="p-button-outlined" icon="pi pi-pencil" onClick={() => editFlag(rowData)} disabled={isDisabled(rowData)} />
    }

    const deleteBody = (rowData) => {
        return <Button className="p-button-outlined p-button-danger" icon="pi pi-trash" onClick={(e) => confirmDeleteFlag(rowData, e)} disabled={isDisabled(rowData)} />
    }

    const reject = () => {
    };

	const confirmDeleteFlag = (flag, e) => {
		confirmPopup({
			target: e.currentTarget,
			message: "Are you sure you want to delete this flag?",
			icon: 'pi pi-info-circle',
			acceptClassName: 'p-button-danger',
			accept: () => deleteFlag(flag),
			reject
		})
	}

    const confirmDeleteAllFlag = (e) => {
		confirmPopup({
			target: e.currentTarget,
			message: "Are you sure you want to delete this flag from every environment?",
			icon: 'pi pi-info-circle',
			acceptClassName: 'p-button-danger',
			accept: () => deleteAllFlags(),
			reject
		})
	}

    const deleteFlag = (flag) => {
		axiosInstance.current.delete("/flags/" + flag.id).then((response) => {
            flipFetchFlag();
		})
		.catch((err) => {
            console.log(err);
			toast.current.show({
				severity: 'error',
				summary: 'Unable to delete flag!',
				detail: err.response ? err.response.data : ''
			});
		})
	}   
    
    const deleteAllFlags = () => {
		axiosInstance.current.delete("/flags/all/" + id).then((response) => {
            navigate("/projects/" + flags[0].projectId);
		})
		.catch((err) => {
            console.log(err);
			toast.current.show({
				severity: 'error',
				summary: 'Unable to delete flags!',
				detail: err.response ? err.response.data : ''
			});
		})
	}

    const isDisabled = (flag) => {
        if (flag.role === 'VIEWER') {
            return true;
        }
        if (flag.environmentProtected && (flag.role === 'USER' || flag.role === 'VIEWER')) {
            return true;
        }
        return false;
    }

    React.useEffect(() => {
        setLoading(true);
        axiosInstance.current.get("/flags/flag-id/" + id).then((response) => {
            setFlags(response.data);
        }).catch((err) => {
            console.error(err);
        }).finally(() => {
            setLoading(false);
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchFlag]);

    return (
        <div>
            {loading ?
            <ProgressWrapper />
            :
            <Container>
                <Toast ref={toast}/>
                <CreateEditFlag flag={selectedFlag} visible={createVisible} hide={toggleVisible} displayToast={displayToast} environmentId={selectedFlag?.environmentId} />
                <FlagDetails visible={detailsVisible} hide={toggleDetails} flag={selectedFlag} flipFetchFlag={flipFetchFlag} apiKey={""} toggleFlag={toggleFlag} />
                <nav>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item" onClick={() => navigate("/")}><FontAwesomeIcon icon={faHome} /></li>
                    <li className="breadcrumb-item" onClick={() => navigate("/projects")}>Projects</li>
                    <li className="breadcrumb-item" onClick={() => navigate("/projects/" + flags[0]?.projectId)}>{flags[0]?.projectName}</li>
                    <li className="breadcrumb-item active">{flags[0]?.name}</li>
                </ol>
                </nav>
                <div className="flex-row title">
                    <div>{flags[0].name}</div>
                </div>
                <div className="secondary-title pt20">
                    <span className="pointer" onClick={() => navigate("/projects/" + flags[0]?.projectId)}><FontAwesomeIcon icon={faLink} /> View Project</span>
                </div>
                <div className="mt20">
                    <div className="secondary-title">
                        <div>Flag ID</div>
                    </div>
                    <div>
                        {id}
                    </div>
                </div>
                <div className="mt20">
                    {!isDisabled(flags[0]) && <Button className="p-button-outlined p-button-danger" label="Delete All Flags" onClick={(e) => confirmDeleteAllFlag(e)} disabled={isDisabled(flags[0])} />}
                </div>
                <div className="mt20">
                    <div className="secondary-title">
                        <div>Environments</div>
                    </div>
                    <DataTable
                        value={flags}
                        rows={5}
                        paginator
                        responsiveLayout="stack"
                        size="small"
                        dataKey="id"
                    >
                        <Column body={environmentNameBody} header="Environment"></Column>
                        <Column field="name" header="Name"></Column>
                        <Column body={enabledBody} header="Enabled"></Column>
                        <Column body={enabledLastBody} header="Date Enabled"></Column>
                        <Column body={archivedBody} header="Archived"></Column>
                        <Column body={viewBody}></Column>
                        <Column body={editBody}></Column>
                        <Column body={deleteBody}></Column>
                    </DataTable>
                </div>
            </Container>
            }
        </div>
    )
}